import { Box, Text, Button, styled, alpha } from '@ltvco/refresh-lib/theme';

export interface CardContainerProps {
  isHovered?: boolean;
  hoverColor?: 'grey50' | 'grey100';
}

export const CardContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isHovered' && prop !== 'hoverColor',
})<CardContainerProps>(({ theme, isHovered, hoverColor = 'grey100' }) => {
  const hoverBgColor =
    hoverColor === 'grey50' ? theme.palette.grey[50] : theme.palette.grey[100];

  return {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: '20px',
    borderRadius: '20px',
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: isHovered ? hoverBgColor : theme.palette.common.white,
    transition: 'background-color 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: hoverBgColor,
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: '16px',
    },
  };
});

export const ContentContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  flex: 1,
  minWidth: 0,
});

export const HeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  width: '100%',
  flexWrap: 'wrap',
  [theme.breakpoints.down('sm')]: {
    '& > *:last-child': {
      width: '100%',
      marginTop: theme.spacing(0.5),
    },
  },
}));

export interface CardIconContainerProps {
  size?: number;
  mobileSize?: number;
}

export const CardIconContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'size' && prop !== 'mobileSize',
})<CardIconContainerProps>(({ theme, size = 24, mobileSize }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: `${size}px`,
  height: `${size}px`,
  borderRadius: '100px',
  backgroundColor: alpha(theme.palette.primary.main, 0.1),
  flexShrink: 0,
  '& svg': {
    fontSize: '16px',
    color: theme.palette.primary.main,
  },
  [theme.breakpoints.down('sm')]: {
    width: `${mobileSize || size}px`,
    height: `${mobileSize || size}px`,
  },
}));

export const CardTitle = styled(Text)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: 'normal',
  color: theme.palette.text.primary,
}));

export const CourtCitation = styled(Text)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '16px',
  color: theme.palette.text.secondary,
}));

export const DetailsContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  width: '100%',
});

export const DetailRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
  width: '100%',
});

export interface DetailLabelProps {
  minWidth?: string;
  mobileMinWidth?: string;
}

export const DetailLabel = styled(Text, {
  shouldForwardProp: (prop) => prop !== 'minWidth' && prop !== 'mobileMinWidth',
})<DetailLabelProps>(({ theme, minWidth = '70px', mobileMinWidth }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '14px',
  fontWeight: 700,
  lineHeight: '16px',
  color: theme.palette.text.secondary,
  minWidth,
  flexShrink: 0,
  [theme.breakpoints.down('sm')]: {
    minWidth: mobileMinWidth || minWidth,
  },
}));

export const DetailValue = styled(Text)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '16px',
  color: theme.palette.text.secondary,
}));

export const MoreCount = styled('span')(({ theme }) => ({
  color: theme.palette.text.primary,
  fontWeight: 600,
}));

export const ViewButton = styled(Button)(({ theme }) => ({
  borderRadius: '32px',
  border: `1px solid ${theme.palette.primary.main}`,
  backgroundColor: 'transparent',
  color: theme.palette.primary.main,
  fontFamily: theme.typography.fontFamily,
  fontSize: '13px',
  fontWeight: 700,
  lineHeight: '22px',
  letterSpacing: '0.46px',
  textTransform: 'capitalize',
  padding: '4px 10px',
  minWidth: 'auto',
  flexShrink: 0,
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    borderColor: alpha(theme.palette.primary.main, 0.5),
  },
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

export const OpinionSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '100%',
});

export const OpinionTypeLabel = styled(Text)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '14px',
  fontWeight: 700,
  lineHeight: '16px',
  color: theme.palette.text.secondary,
}));

export const OpinionSnippet = styled(Text)(({ theme }) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '16px',
  color: theme.palette.text.secondary,
  display: '-webkit-box',
  WebkitLineClamp: 1,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}));
